/**
 * Creative-inventory catalogue, generated from the live registries (blocks, items, spawn
 * eggs, potions, enchantments) so anything registered later shows up automatically.
 * DOM-free (smoke-tested). The creative screen only decides where each group is shown.
 *
 * Groups: 0 building, 1 natural, 2 functional, 3 tools & combat, 4 food & ingredients,
 * 5 spawn eggs, 6 potions, 7 enchanted books.
 */

import { BLOCKS, PLACEABLE_IDS, RenderShape } from '../blocks';
import { ITEM_IDS } from '../items';
import { itemDef } from './itemdb';
import { I } from './itemIds';
import { isSpawnEgg } from './spawnEggs';
import { BREW_MIXES, POTIONS, potionByKey } from './brewing/potions';
import { ENCHANTS } from './enchant/registry';
import { cloneStack, type Stack } from './types';

export const NATURAL = /grass block|dirt|podzol|mycelium|path$|farmland|^mud$|sand$|gravel|clay|log$|leaves|ore$|debris|ice$|snow|moss|cactus|mushroom|pumpkin|^melon|hay|sponge|netherrack|soul s|basalt|tuff|calcite|dripstone|sculk|^deepslate$|^stone$|granite$|diorite$|andesite$|obsidian|bedrock|magma|coral|kelp|seagrass|bamboo|cobweb|shroomlight|sapling|fern|bush|tulip|orchid|allium|bluet|daisy|cornflower|lily|poppy|dandelion|sunflower|lilac|peony|azalea|petals|litter|wildflowers|vines|short grass|wheat|sugar cane|amethyst$|^glowstone$/i;
export const FUNCTIONAL = /crafting table|furnace|chest|torch|^tnt$|bookshelf|door$|bed$|^rail|jack o|lantern|composter|lectern|barrel|fletching|grindstone|smoker|stonecutter|smithing|brewing/i;

export const G = { BUILDING: 0, NATURAL: 1, FUNCTIONAL: 2, TOOLS: 3, FOOD: 4, EGGS: 5, POTIONS: 6, BOOKS: 7 } as const;

export function groupOfBlock(id: number): number {
  const d = BLOCKS[id];
  if (FUNCTIONAL.test(d.name)) return G.FUNCTIONAL;
  if (d.shape === RenderShape.Cross || d.shape === RenderShape.Flat || NATURAL.test(d.name)) return G.NATURAL;
  return G.BUILDING;
}

export function groupOfItem(id: number): number {
  if (isSpawnEgg(id)) return G.EGGS;
  const d = itemDef(id);
  if (d.tool || d.armor || id === I.BOW || id === I.ARROW || id === I.BUCKET || id === I.WATER_BUCKET ||
      id === I.FLINT_AND_STEEL || id === I.SADDLE || id === I.SHEARS) return G.TOOLS;
  return G.FOOD;
}

const FORMS = [I.POTION, I.SPLASH_POTION, I.LINGERING_POTION];

/** Potion indices that exist in the recipe graph: the bases plus every brewable result. */
export function brewablePotionIndices(): number[] {
  const set = new Set<number>(POTIONS.filter((p) => p.base).map((p) => p.index));
  for (const [, , to] of BREW_MIXES) set.add(potionByKey(to).index);
  return [...set].sort((a, b) => a - b);
}

/** Every potion in every form (regular, splash, lingering), grouped by form then potion order. */
export function potionStacks(): Stack[] {
  const out: Stack[] = [];
  const idx = brewablePotionIndices();
  for (const form of FORMS) for (const dmg of idx) out.push({ id: form, count: 1, dmg });
  return out;
}

/** One enchanted book per enchantment per level (treasure and curses included). */
export function bookStacks(): Stack[] {
  const out: Stack[] = [];
  for (const e of ENCHANTS) {
    if (!e) continue;
    for (let lvl = 1; lvl <= e.max; lvl++) out.push({ id: I.ENCHANTED_BOOK, count: 1, dmg: 0, ench: [{ id: e.id, lvl }] });
  }
  return out;
}

const SPECIAL = new Set<number>([I.POTION, I.SPLASH_POTION, I.LINGERING_POTION, I.ENCHANTED_BOOK]);

let cache: Stack[][] | null = null;
/** Stack templates per group (see G). Treat the stacks as read-only; clone before handing out. */
export function creativeCatalog(): Stack[][] {
  if (cache) return cache;
  const g: Stack[][] = [[], [], [], [], [], [], [], []];
  const blocks = new Set<number>(PLACEABLE_IDS);
  for (const id of [260, 276, 280, 284]) if (BLOCKS[id]) blocks.add(id); // chest / doors / bed
  for (const id of [...blocks].sort((a, b) => a - b)) g[groupOfBlock(id)].push({ id, count: 1, dmg: 0 });
  for (const id of ITEM_IDS) if (!SPECIAL.has(id)) g[groupOfItem(id)].push({ id, count: 1, dmg: 0 });
  g[G.POTIONS] = potionStacks();
  g[G.BOOKS] = bookStacks();
  cache = g;
  return g;
}

export const cloneTemplate = (s: Stack): Stack => cloneStack(s);
